<script setup lang="ts">
    import { CardTitle } from '@/components/ui/card';
    import { columns } from '@/tables/columns/salary-status';
    import { computed, ref } from 'vue';
    import { Head } from '@inertiajs/vue3';
    import { usePage } from '@inertiajs/vue3';
    import { useQuery } from '@tanstack/vue-query';
    import { useRoles } from '@/composables/useRoles';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import DataTable from '@/components/datatable/DataTable.vue';
    import MonthPicker from '@/components/MonthPicker.vue';
    import PageCard from '@/components/PageCard.vue';
    import QueryKey from '@/enums/QueryKey';
    import Role from '@/enums/Role';
    import TableTypeSelect from '@/components/datatable/DataTableTypeSelect.vue';
    import type { SharedData, BreadcrumbItem } from '@/types';

    const { hasAnyRole } = useRoles();
    const page = usePage<SharedData>();
    const isClient = computed(() => page.props.auth.user.is_client);

    const props = defineProps<{
        reporting_full_date: string;
    }>();

    const selectedGrouping = ref('my_clients');
    const date = ref(page.props.auth.user.reporting_full_date || props.reporting_full_date);

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'dashboards.payroll',
        },
        {
            title: 'dashboards.employee.payroll_header',
        },
    ];

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.SALARY_STATUS, { selectedGrouping, date }],
        queryFn: getSalaryStatuses,
    });

    async function getSalaryStatuses() {
        const { data } = await axios.get(
            route('v2.salary.status.table', {
                reportingPeriod: date.value,
                grouping: selectedGrouping.value,
            }),
        );
        return data.data;
    }

    const typeOptions = [
        { value: 'my_clients', label: 'dashboards.grouping.my_clients' },
        { value: 'all', label: 'dashboards.grouping.all' },
    ];
</script>

<template>
    <Head :title="$t('dashboards.employee.payroll_header')" />

    <AppLayout
        :breadcrumbs="breadcrumbs"
        header-class="w-full"
    >
        <template
            #header-actions
            v-if="!isClient"
        >
            <span class="ml-auto">
                <MonthPicker v-model="date" />
            </span>
        </template>
        <PageCard>
            <DataTable
                :table-key="QueryKey.SALARY_STATUS"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :default-sorting="[
                    {
                        id: 'name',
                        desc: false,
                    },
                ]"
            >
                <template #title>
                    <CardTitle>
                        {{ $t('dashboards.employee.payroll_header') }}
                    </CardTitle>
                </template>
                <template
                    #actions
                    v-if="hasAnyRole([Role.SUPER_ADMIN, Role.PAYROLL])"
                >
                    <TableTypeSelect
                        v-model="selectedGrouping"
                        :options="typeOptions"
                    />
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
